/**
 * AS-10941 — which selected rows the "Reset to Shopify Cost" call addresses.
 *
 * Assigning a product copies that day's Shopify "Cost per item" onto the link,
 * and the PO cost ladder then treats it as the negotiated price — so the row
 * keeps its link-day cost however often the merchant edits Shopify afterwards.
 * The reset re-reads it server-side; this only decides what to ask about.
 *
 * Kept as a pure module rather than a method on SupplierDetail.vue so the rule
 * is testable on its own.
 */

/** The fields of a supplier-products table row this reads. */
export interface CostResetRow {
  /** The supplier-product LINK id. A row without one has nothing to update. */
  id?: number | null;
  variant_id?: number | string;
}

/** Whether this row is one the reset can address at all. */
function hasSupplierLink(row: CostResetRow): boolean {
  return row?.id != null && Number(row.id) > 0;
}

/**
 * Rows the reset cannot address, because they carry no supplier-product link
 * id — the list maps some rows straight from report data.
 *
 * Counted separately so the caller can say how many products it left alone
 * instead of dropping them from a selection it promised to reset.
 */
export function countUnlinkedRows(rows: CostResetRow[]): number {
  if (!Array.isArray(rows)) { return 0; }
  return rows.filter(row => !hasSupplierLink(row)).length;
}

/**
 * Variant ids to send, deduplicated.
 *
 * Rows with no link id are dropped — see countUnlinkedRows, which the caller
 * reports. Whether a variant can actually be priced is the backend's call: it
 * skips the ones the report does not price yet rather than writing a zero over
 * a real cost.
 */
export function buildCostResetVariantIds(rows: CostResetRow[]): Array<number | string> {
  if (!Array.isArray(rows)) { return []; }
  const seen = new Set<string>();
  const ids: Array<number | string> = [];
  for (const row of rows) {
    if (!hasSupplierLink(row)) { continue; }
    const variantId = row?.variant_id;
    if (variantId == null || variantId === '') { continue; }
    const key = String(variantId);
    // A row can appear twice when a selection survives a page change.
    if (seen.has(key)) { continue; }
    seen.add(key);
    ids.push(variantId);
  }
  return ids;
}
